import {
  socketMessageReceivedUnhandledAction,
  socketMessageUsersViewingDocumentAction,
  documentForceSyncErpCompletedAction,
  documentPublishCompletedAction,
} from '@/domain/ws/actions';
import { erpSyncCompletedAction } from '@/domain/settings/settingsActions';
import {
  bankFeedConnectionsHandleWebSocketAction,
  bankFeedTransactionExportCompletedAction,
  bankFeedConnectionSetupCompletedAction,
} from '@/domain/bankFeed/actions';
import { documentCurrentViewersAdapter } from '@/domain/documents/adapters';
import { WEBSOCKET_MESSAGE_TYPES } from '@/domain/bankFeed/actions.types';
import {
  BankFeedTransactionExportEventMetadataSchema,
  BankFeedConnectionSetupCompletedMetadataSchema,
} from '@/domain/bankFeed/schemas';
import { ChatUnreadUpdateSchema } from '@/domain/chat/schemas';
import { setTotalUnreadFromWsAction } from '@/domain/chat/chatActions';

export const WSMassageToActionMapper = (message) => {
  const { event, metadata } = message;
  switch (event) {
    case 'USERS_VIEWING_DOCUMENT': {
      return socketMessageUsersViewingDocumentAction(documentCurrentViewersAdapter(metadata.user_ids));
    }
    case 'ERP_SYNC_COMPLETED': {
      return erpSyncCompletedAction(metadata);
    }
    case 'DOCUMENT_FORCE_SYNC_ERP_COMPLETED': {
      return documentForceSyncErpCompletedAction(metadata);
    }
    case 'DOCUMENT_PUBLISH_COMPLETED': {
      return documentPublishCompletedAction(metadata);
    }
    case 'BANK_CONNECTION_CREATED': {
      return bankFeedConnectionsHandleWebSocketAction({
        type: WEBSOCKET_MESSAGE_TYPES.ADD,
        connectionIDs: metadata?.connection_ids,
      });
    }
    case 'BANK_CONNECTION_UPDATED': {
      return bankFeedConnectionsHandleWebSocketAction({
        type: WEBSOCKET_MESSAGE_TYPES.UPDATE,
        connectionIDs: metadata?.connection_ids,
      });
    }
    case 'BANK_CONNECTION_DELETED': {
      return bankFeedConnectionsHandleWebSocketAction({
        type: WEBSOCKET_MESSAGE_TYPES.REMOVE,
        connectionIDs: metadata?.connection_ids,
      });
    }
    case 'TRANSACTION_EXPORT_COMPLETED': {
      const parsed = BankFeedTransactionExportEventMetadataSchema.safeParse(metadata);
      if (!parsed.success) {
        console.error('[WS] TRANSACTION_EXPORT_COMPLETED invalid payload', parsed.error.issues, metadata);
        return socketMessageReceivedUnhandledAction({ event, metadata });
      }
      return bankFeedTransactionExportCompletedAction(parsed.data);
    }
    case 'BANK_CONNECTION_SETUP_COMPLETED': {
      const parsed = BankFeedConnectionSetupCompletedMetadataSchema.safeParse(metadata);
      if (!parsed.success) {
        console.error('[WS] BANK_CONNECTION_SETUP_COMPLETED invalid payload', parsed.error.issues, metadata);
        return socketMessageReceivedUnhandledAction({ event, metadata });
      }
      return bankFeedConnectionSetupCompletedAction(parsed.data);
    }
    default:
      return socketMessageReceivedUnhandledAction({ event, metadata });
  }
};

export const genericWSMessageHandlerFactory = (mapper) => (dispatch) => (message) => dispatch(mapper(message));

export const wsMessageHandlerFactory = genericWSMessageHandlerFactory(WSMassageToActionMapper);

// Closed-chat unread updates: the backend forwards Sendbird events over Azure Web
// PubSub. Each `CHAT_UNREAD_UPDATE` carries the authoritative workspace-wide
// `metadata.total_unread`, so while the chat drawer is closed we use it to keep the
// header chat-icon TOTAL counter live — no delta math (the old delta reducer drifted
// and was removed).
//
// Scope: TOTAL only. The event carries context for the single changed channel
// (`metadata.data`), NOT authoritative per-company/per-document totals, so the
// per-company/document counters are intentionally left untouched here (updating them
// from this event would require the banned delta arithmetic). Those stay driven by the
// open-drawer GroupChannelCollection / REST seed — see the deferred tasks in the
// chat-v2-unread-counters-from-collection OpenSpec change.
export const chatNotificationsMessageHandler = (dispatch) => (message) => {
  if (message?.event !== 'CHAT_UNREAD_UPDATE') return;
  const parsed = ChatUnreadUpdateSchema.safeParse(message);
  if (!parsed.success) {
    // Only a missing/invalid `total_unread` reaches here (other fields are optional).
    // Skip this one event and keep the last-known total; the next valid event corrects it.
    // Log only the Zod issues, not the raw message — payloads can be large / carry PII.
    console.error('[chatNotifications] invalid CHAT_UNREAD_UPDATE payload', parsed.error.issues);
    return;
  }
  dispatch(setTotalUnreadFromWsAction(parsed.data.metadata.total_unread));
};
